import React from 'react';
import { useAuth } from '@clerk/nextjs';
import Head from 'next/head';
import Router from 'next/router';
import { NextSeo } from 'next-seo';
import nProgress from 'nprogress';

import { LoadingContext } from 'components/Loading';
import useParityUser, { USER_PREFETCH_URL } from 'data/useParityUser';
import decorateStatics from 'lib/hoc/decorateStatics';
import { NextStaticPageConfig, NextWrappedPage } from 'types';
import { AuthRole } from 'types/enums';

import getRedirect from './redirect';

const isAnonymousRole = (role: AuthRole) => [AuthRole.Anonymous, AuthRole.All].includes(role);

const staticPage =
  <P extends object, IP = {}>(config: NextStaticPageConfig = {}) =>
  (Component: NextWrappedPage<P, IP>) => {
    const role = config.role || AuthRole.All;

    const WrappedComponent: React.FC<P> = (props) => {
      const [loaded, setLoaded] = React.useState(true);
      const loadedRef = React.useRef(true);

      const { isLoaded: clerkLoaded, isSignedIn } = useAuth();
      const { user, isValidating } = useParityUser();

      React.useEffect(() => {
        if (!clerkLoaded || isValidating) {
          return;
        }
        // Treat a Clerk-signed-out session as "no user" even if SWR still has a
        // cached `/users/me` payload — fixes cross-tab sign-out: when another
        // tab clears the Clerk session, this effect must re-evaluate and redirect.
        const effectiveUser = isSignedIn ? user : undefined;
        const redirectTo = getRedirect({ role, user: effectiveUser });
        // Strip both query string AND hash before comparing — Clerk's SignIn uses
        // hash routing (`/signin#/factor-one`), so `asPath.split('?')[0]` alone
        // would still differ from `/signin` and trigger a redundant Router.replace.
        if (redirectTo && Router.asPath.split(/[?#]/)[0] !== redirectTo) {
          Router.replace(redirectTo);
        }
      }, [clerkLoaded, isValidating, user, role, isSignedIn]);

      React.useEffect(() => {
        if (!nProgress.isStarted() && ((!user && !isAnonymousRole(role)) || !loaded)) {
          nProgress.start();
        }
      }, [user]);

      React.useEffect(() => {
        if (
          user &&
          ((process.env.NODE_ENV === 'development' && nProgress.isStarted()) || process.env.NODE_ENV === 'production')
        ) {
          if (loadedRef.current) {
            nProgress.done();
          } else {
            nProgress.inc(0.5);
          }
        }

        if (!user && isAnonymousRole(role)) {
          if (loadedRef.current) {
            nProgress.done();
          } else {
            nProgress.inc(0.5);
          }
        }
      });

      const setLoadedMemo = React.useCallback(
        (to: boolean) => {
          setLoaded(to);
          loadedRef.current = to;
        },
        [setLoaded]
      );

      return (
        <>
          <Head>
            {!isAnonymousRole(role) && (
              <link
                rel="preload"
                href={USER_PREFETCH_URL}
                as="fetch"
                crossOrigin="use-credentials"
                type="application/json"
              />
            )}
          </Head>
          {config.title && <NextSeo title={config.title} />}
          <LoadingContext.Provider value={setLoadedMemo}>
            {user || [AuthRole.Anonymous, AuthRole.All].includes(role) ? (
              <Component {...props} />
            ) : (
              <div style={{ flex: '1 0 auto' }} />
            )}
          </LoadingContext.Provider>
        </>
      );
    };

    return decorateStatics<P, IP>(config)(WrappedComponent);
  };

export default staticPage;
